/**
 * The user interface, inside the headset.
 *
 * DOM does not render in an immersive WebXR session. Every overlay this game
 * has — the prompt, the vitals, subtitles, toasts, the map, the menus, the
 * level card — is DOM, so in a headset the player currently gets none of it and
 * cannot pick anything up. This layer is the answer: the same content, read off
 * the same event bus, painted onto canvases and hung in the room as quads.
 *
 * Three properties it has to keep:
 *
 *  1. **Nothing exists until a session does.** Constructing this costs seven
 *     event subscriptions and one object. No canvas, no texture, no mesh and no
 *     scene node is created until `renderer.xr.isPresenting` first goes true,
 *     and the whole group comes out of the scene the moment it goes false — so
 *     a desktop frame does one boolean read and returns. That is the bargain
 *     `net/client.ts` already makes for the shared world, for the same reason:
 *     this game is played every day on a monitor and that must not change.
 *  2. **It reads, it does not own.** Prompt text, subtitles and toasts arrive
 *     as events. Screen state is read from `ui/ui.ts`. Menu presses go back out
 *     as the identical `UIAction`s the DOM buttons dispatch. There is no second
 *     source of truth anywhere in here.
 *  3. **It is comfortable.** Panels are body-locked and lazily following rather
 *     than welded to the face, they are pulled in so they never sit inside a
 *     wall, and the type is authored large enough to be resolved rather than
 *     guessed at. See `dock.ts` and `panel.ts`.
 *
 * Ordering note: `ui.update` runs after `render.render()`, so the head pose
 * this layer places against is the one that was just drawn with, and the
 * placement it writes lands on the next frame. On a damped, body-locked dock at
 * 72–90 Hz that is invisible; on anything head-locked it would not be, which is
 * one more reason nothing here is.
 */

import * as THREE from 'three';
import type { JournalEntry, ResumeOffer, ScreenName, UIAction } from '../../core/systems';
import type { ComfortHost, EventBus, GameContext, GameSettings, InventorySlot, LevelSpec } from '../../core/types';
import { Dock, handGrip } from './dock';
import { PromptPanel, VitalsPanel } from './hud';
import { LevelCardPanel, SubtitlePanel, ToastPanel } from './messages';
import { MenuPanel, type MenuModel, type RowHit } from './menu';
import { hitQuad, MirrorPanel, PressMark, type QuadHit } from './panel';
import { XRPointer, type XRRay } from './pointer';
import { buildScreen, comfortScreen, type DeathResult, type ScreenDeps } from './screens';
import { ComfortTab } from './tab';

/* ------------------------------------------------------------------ *
 * Geometry of the docks
 *
 * Everything below is in metres at the dock's design distance, and every angle
 * quoted is measured from the eye line. Panels sit low and to the sides: the
 * middle of the view belongs to the building.
 * ------------------------------------------------------------------ */

/**
 * Metres in front of the player everything settles at.
 *
 * One design distance for the whole layer, deliberately. Every panel size and
 * every type size in `ui/xr/` is a length in metres *at this distance*, so one
 * number converts any of them to an angle (1° is 21.8 mm here) and two panels
 * can never end up authored against different scales. It is also a comfortable
 * reading distance: near enough to resolve small type, far enough that the eyes
 * are not converging hard on it.
 */
const CONTENT_DISTANCE = 1.25;
/** Metres below eye level the readout dock's origin sits — about 14°. */
const CONTENT_DROP = 0.3;
/** Menus sit nearer the eye line than the readouts: they are read, not glanced at. */
const MENU_DISTANCE = CONTENT_DISTANCE;
const MENU_DROP = 0.12;

/** Local offsets on the content dock. */
const PROMPT_AT = new THREE.Vector3(0, 0.02, 0);
const VITALS_AT = new THREE.Vector3(-0.45, -0.06, 0);
const TOASTS_AT = new THREE.Vector3(0.45, 0.16, 0);
const SUBTITLE_AT = new THREE.Vector3(0, -0.24, 0);
const CARD_AT = new THREE.Vector3(0, CONTENT_DROP, 0);
/** Where the corner map hangs when there is no hand to put it on. */
const MAP_CORNER_AT = new THREE.Vector3(0.47, -0.16, 0);
/**
 * The comfort plate, below the vitals and outside the subtitle's block. Low and
 * left is where this game's instruments already live; see `tab.ts` for why
 * there has to be one at all.
 */
const TAB_AT = new THREE.Vector3(-0.545, -0.25, 0);

/**
 * Metres of slop allowed around a target when a ray is tested against it.
 *
 * These are the generosity the brief asks for, in the only units that mean
 * anything: at the dock's design distance 0.05 m is about 2.3°, which is a
 * little more than the error in a gaze ray. The plate gets slightly less
 * because it is the one target that is live while the player is walking around,
 * and a press that grazes it should not stop the game.
 */
const MENU_MARGIN = 0.05;
const TAB_MARGIN = 0.035;

/** Height of the map when it takes the middle of the view. */
const MAP_FULL_HEIGHT = 0.66;
/** Height of the corner map, docked. */
const MAP_CORNER_HEIGHT = 0.3;
/** Height of the map on the wrist, which is about 40 cm from the eye. */
const MAP_WRIST_HEIGHT = 0.15;
/** Map texture uploads per second while it is up. See `MirrorPanel.refresh`. */
const MAP_REFRESH_HZ = 12;

type MapMount = 'off' | 'wrist' | 'corner' | 'full';

/**
 * Everything `layer.ts` needs from `ui/ui.ts`.
 *
 * Deliberately a bundle of accessors rather than a reference to the `UI` class:
 * the in-world layer must not be able to reach into the DOM UI's internals, and
 * `ui/ui.ts` is the only existing file this batch touches at all.
 */
export interface XRUiHost {
  readonly events: EventBus;
  /** The screen the DOM UI is showing. */
  readonly current: ScreenName;
  /** The live settings object the DOM UI is holding. */
  readonly settings: GameSettings;
  /** The map overlay's canvas, once it has one. */
  mapCanvas(): HTMLCanvasElement | null;
  /** The map is up in either form. */
  readonly mapVisible: boolean;
  /** The map is up full-screen, which halts the simulation. */
  readonly mapFull: boolean;

  dispatch(a: UIAction): void;
  show(screen: ScreenName): void;
  openSettings(): void;
  closeSettings(): void;
  openJournal(): void;
  closeJournal(): void;
  applySettings(s: GameSettings): void;
  /**
   * Comfort — turn style, tunnel, handedness. `render/xr.ts` implements it and
   * persists every write itself. The in-world settings screen is the only place
   * it can be edited, because deciding whether 30° per snap is right is
   * something you have to be wearing the headset to do.
   */
  readonly comfort: ComfortHost;
}

export interface XRUi {
  update(ctx: GameContext): void;
  /** The settings changed, from either screen. */
  settingsChanged(): void;
  setResume(offer: ResumeOffer | null): void;
  setJournal(notes: readonly JournalEntry[]): void;
  /**
   * True when a ray is pointing at something in here that a press would
   * operate.
   *
   * Offered for whoever owns locomotion. On a device whose ray only exists
   * during a pinch, the *same* pinch that presses a panel is also the pinch
   * that teleports — the panels are drawn last in the frame and cannot get in
   * front of that on their own. This is the seam that lets the movement code
   * ask, before it acts, whether the player was reaching for a menu.
   *
   * Takes `XRIntent.ray`'s shape and nothing else. False whenever no session is
   * running, so it costs one boolean on any frame that is not in a headset.
   */
  claimsRay(ray: { origin: THREE.Vector3; direction: THREE.Vector3 } | null): boolean;
  dispose(): void;
}

/** Every three.js object the layer owns. Built on the first session, once. */
interface Built {
  root: THREE.Group;
  content: Dock;
  menuDock: Dock;
  prompt: PromptPanel;
  vitals: VitalsPanel;
  subtitle: SubtitlePanel;
  toasts: ToastPanel;
  card: LevelCardPanel;
  menu: MenuPanel;
  tab: ComfortTab;
  mark: PressMark;
  map: MirrorPanel | null;
}

class XRUiLayer implements XRUi {
  private built: Built | null = null;
  private active = false;
  private lastNow = 0;
  private menuWasOpen = false;

  private readonly off: Array<() => void> = [];

  /* -- Content, cached off the bus so entering a session finds it already -- */
  private prompt: string | null = null;
  private lastSpec: LevelSpec | null = null;
  private lastIndex = 0;
  private death: DeathResult | null = null;

  /* -- Menu state -- */
  private lastScreen: ScreenName | null = null;
  private model: MenuModel | null = null;
  /** The model the panel is currently showing, for an identity compare. */
  private appliedModel: MenuModel | null = null;
  private modelDirty = true;
  private resume: ResumeOffer | null = null;
  private readonly journal: JournalEntry[] = [];
  private journalIndex = 0;
  private inventory: readonly InventorySlot[] = [];
  /** Cheap fingerprint of the pack, so the inventory screen knows to redraw. */
  private inventoryKey = '';
  private readonly deps: ScreenDeps;

  /* -- The map -- */
  private mapMount: MapMount = 'off';
  private mapWrist: THREE.Object3D | null = null;
  private mapRefreshIn = 0;

  /* -- Pointing -- */
  /**
   * Built at construction because it is a handful of fields and no listeners:
   * it subscribes to a session in `activate` and to nothing at all before that,
   * so a desktop carries an object with seven numbers in it and no more.
   */
  private readonly pointer = new XRPointer();
  /** The comfort panel is up over whatever screen the DOM UI thinks it is on. */
  private quick = false;
  /** It was opened from play, so closing it has to start the run again. */
  private quickFromPlay = false;

  constructor(private readonly host: XRUiHost) {
    // The getters below are read when a row is drawn or pressed, both of which
    // happen long after the model was built, so they have to reach back into
    // the layer rather than close over a copy.
    const self = this;
    this.deps = {
      dispatch: (a) => host.dispatch(a),
      show: (s) => host.show(s),
      openSettings: () => host.openSettings(),
      closeSettings: () => host.closeSettings(),
      openJournal: () => host.openJournal(),
      closeJournal: () => host.closeJournal(),
      applySettings: (s) => host.applySettings(s),
      comfort: host.comfort,
      selectJournal: (i) => {
        this.journalIndex = i;
        this.modelDirty = true;
      },
      openQuick: () => this.openQuick(),
      closeQuick: () => this.closeQuick(),
      showMenu: () => this.showMenu(),
      get quickFromPlay(): boolean {
        return self.quickFromPlay;
      },
      get settings(): GameSettings {
        return host.settings;
      },
      get resume(): ResumeOffer | null {
        return self.resume;
      },
      get journal(): readonly JournalEntry[] {
        return self.journal;
      },
      get journalIndex(): number {
        return self.journalIndex;
      },
      get inventory(): readonly InventorySlot[] {
        return self.inventory;
      },
      get death(): DeathResult | null {
        return self.death;
      },
    };
    this.wire(host.events);
  }

  /* ---------------------------------------------------------------- *
   * Content, from the same bus the DOM UI reads
   * ---------------------------------------------------------------- */

  /**
   * Subscribed at boot rather than on entering a session, because every one of
   * these is edge-triggered: `ui:prompt` fires when the focus *changes*, so a
   * layer that only started listening on session entry would have no idea there
   * was an almond water under the crosshair.
   *
   * The three that would *paint* a canvas are gated on `active` instead, so the
   * only thing a desktop run ever does here is assign a field or push onto a
   * bounded array. The state those three would have carried is re-applied by
   * `activate` from the caches above.
   */
  private wire(events: EventBus): void {
    const on = events.on.bind(events);
    this.off.push(
      on('ui:prompt', ({ text }) => {
        this.prompt = text;
      }),
      on('ui:toast', ({ text, tone }) => {
        if (this.active) this.built?.toasts.add(text, tone ?? 'info');
      }),
      on('ui:subtitle', ({ text, ms, speaker }) => {
        if (this.active) this.built?.subtitle.say(text, ms, speaker);
      }),
      on('ui:note', ({ id, title, body }) => {
        if (this.journal.some((n) => n.id === id)) return;
        this.journal.push({ id, title, body });
        this.journalIndex = this.journal.length - 1;
        this.modelDirty = true;
      }),
      on('level:loaded', ({ spec, index }) => {
        this.lastSpec = spec;
        this.lastIndex = index;
        if (this.active) this.built?.card.set(spec.name, spec.subtitle, spec.blurb);
      }),
      on('game:start', () => {
        this.journal.length = 0;
        this.journalIndex = 0;
        this.death = null;
        this.prompt = null;
        this.built?.toasts.clear();
        this.built?.subtitle.clear();
        this.modelDirty = true;
      }),
      on('game:over', ({ reason, survived, depth }) => {
        this.death = { reason, survived, depth, place: this.levelLabel(depth) };
        this.prompt = null;
        this.modelDirty = true;
      }),
    );
  }

  /** Mirrors `levelLabel` in `ui/ui.ts`. */
  private levelLabel(depth: number): string {
    if (this.lastSpec && this.lastIndex === depth) return `${this.lastSpec.name} — ${this.lastSpec.subtitle}`;
    return `Level ${depth}`;
  }

  settingsChanged(): void {
    this.built?.subtitle.setEnabled(this.host.settings.subtitles);
    this.modelDirty = true;
  }

  setResume(offer: ResumeOffer | null): void {
    this.resume = offer;
    this.modelDirty = true;
  }

  setJournal(notes: readonly JournalEntry[]): void {
    this.journal.length = 0;
    for (const n of notes) this.journal.push(n);
    this.journalIndex = Math.max(0, this.journal.length - 1);
    this.modelDirty = true;
  }

  /* ---------------------------------------------------------------- *
   * The frame
   * ---------------------------------------------------------------- */

  update(ctx: GameContext): void {
    // The gate. One property read on every desktop frame this game will ever
    // run, and then it is gone.
    if (!ctx.render.renderer.xr.isPresenting) {
      if (this.active) this.suspend();
      return;
    }
    const b = this.active ? this.built : this.activate(ctx);
    if (!b) return;

    // Wall clock, not `ctx.dt`: the docks and the fades have to keep moving
    // while a menu holds the simulation at zero.
    const now = performance.now();
    const dt = Math.min(0.1, Math.max(0, (now - this.lastNow) / 1000));
    this.lastNow = now;

    const head = ctx.render.camera;
    const grid = ctx.level ? ctx.level.grid : null;
    this.inventory = ctx.inventory;
    this.togglePack(ctx);

    const screen = this.host.current;
    const mapFull = this.host.mapFull;
    const playing = screen === 'hud' || screen === 'levelcard';

    this.syncModel(screen, mapFull);

    // The menu dock is snapped the frame it is first needed: a pause screen
    // easing in from wherever the last one was left is a distraction at exactly
    // the moment the player wants to read something.
    const menuUp = b.menu.open || mapFull;
    if (menuUp && !this.menuWasOpen) b.menuDock.snap(head, grid);
    else if (menuUp) b.menuDock.update(dt, head, grid);
    this.menuWasOpen = menuUp;

    b.content.update(dt, head, grid);

    // The plate is the only thing in here that is live while the game is being
    // played, and it goes away under a menu because the menu carries the same
    // way in as a row.
    const tabUp = playing && !b.menu.open && !mapFull;
    b.tab.update(dt, tabUp);

    // Pointing, before anything is driven by it. The docks have just moved and
    // three only recomposes world matrices when it draws, so the subtree has to
    // be brought up to date or every hit test is against where the panels were
    // a frame ago — which, the frame a menu snaps into place, is a long way off.
    b.root.updateMatrixWorld(true);
    this.pointer.sample(dt);
    b.menu.setInputStyle(this.pointer.inputStyle);
    b.prompt.setInputStyle(this.pointer.inputStyle);
    const pointerTook = this.point(b, dt, tabUp);

    if (b.menu.open && b.menu.step(ctx.input, dt, pointerTook)) this.modelDirty = true;
    b.menu.update(dt);
    b.mark.update(dt);

    // The readouts. Hidden under a menu, exactly as `ui/ui.ts` hides the DOM
    // HUD for every screen that is not the HUD or a level card.
    b.prompt.set(this.prompt);
    b.prompt.update(dt, playing && !b.menu.open);
    b.vitals.sample(ctx);
    b.vitals.update(dt, playing && !b.menu.open);
    b.subtitle.tick(ctx.dt);
    b.subtitle.update(dt, !b.menu.open);
    b.toasts.tick(ctx.dt);
    b.toasts.update(dt, !b.menu.open);
    b.card.update(dt, screen === 'levelcard');

    this.syncMap(ctx, dt, mapFull);
  }

  /* ---------------------------------------------------------------- *
   * Pointing
   *
   * One press, resolved against whatever is up. The order below is the whole
   * design: a hover is optional and only ever moves a cursor, a press selects
   * and acts in the same frame, and a press that landed on a panel is *taken*
   * so that the same pinch cannot also press whatever the cursor was left on.
   * ---------------------------------------------------------------- */

  /** Returns true when this frame's press belongs to a panel. */
  private point(b: Built, dt: number, tabUp: boolean): boolean {
    const pointer = this.pointer;
    const menuOpen = b.menu.open;

    // Hover, where there is a continuous ray to hover with. Vision Pro never
    // reaches this and must never need to.
    if (menuOpen && pointer.aim) b.menu.hover(this.rowUnder(b, pointer.aim));
    else if (menuOpen) b.menu.hover(null);

    if (pointer.released) b.menu.release();
    else if (pointer.held && b.menu.holdStep(dt)) this.modelDirty = true;

    const press = pointer.press;
    if (!press) return false;

    const onMenu = menuOpen ? hitQuad(b.menu.panel.object, press, MENU_MARGIN) : null;
    const onTab = tabUp ? hitQuad(b.tab.panel.object, press, TAB_MARGIN) : null;

    if (onMenu && (!onTab || onMenu.distance <= onTab.distance)) {
      b.mark.show(b.menu.panel.object, onMenu.u, onMenu.v);
      const row = b.menu.hit(onMenu);
      if (row && b.menu.press(row)) this.modelDirty = true;
      // Taken even when it found no row: the player aimed at this panel, and
      // spending their press on the row a stick happened to be resting on is
      // the one outcome they cannot have meant.
      return true;
    }
    if (onTab) {
      b.mark.show(b.tab.panel.object, onTab.u, onTab.v);
      this.openQuick();
      return true;
    }
    // A press that hit nothing. On a device whose only ray is the pinch there
    // is no cursor for it to have meant, so it is dropped; on a controller it
    // falls through to the trigger, which is what the stick-and-trigger design
    // has always done and what a Quest player expects.
    return this.pointer.inputStyle === 'point';
  }

  private rowUnder(b: Built, ray: XRRay): RowHit | null {
    const at: QuadHit | null = hitQuad(b.menu.panel.object, ray, MENU_MARGIN);
    return at ? b.menu.hit(at) : null;
  }

  /**
   * Whether a ray would land on something pressable. See `XRUi.claimsRay`.
   *
   * Deliberately answers for the state of *this* frame rather than predicting:
   * the caller is asking at the top of a frame about panels placed at the end
   * of the last one, and at 72–90 Hz on a damped dock those are the same place.
   */
  claimsRay(ray: { origin: THREE.Vector3; direction: THREE.Vector3 } | null): boolean {
    const b = this.built;
    if (!this.active || !b || !ray) return false;
    if (b.menu.open && hitQuad(b.menu.panel.object, ray, MENU_MARGIN)) return true;
    return b.tab.panel.visible && hitQuad(b.tab.panel.object, ray, TAB_MARGIN) !== null;
  }

  /* ---------------------------------------------------------------- *
   * Menus
   * ---------------------------------------------------------------- */

  /**
   * Open the comfort panel, and stop the building while it is up.
   *
   * The pause is not incidental. This is reached by somebody who has just
   * realised they feel ill, or by somebody who has realised they cannot move
   * the way they expected to; asking them to read a menu with a floor still
   * under them is asking them to do the thing that made them ill. The DOM
   * pause screen is shown underneath so the halt goes through exactly the path
   * every other menu uses — `ui/ui.ts` owns `blocking`, and `game.ts` reads it.
   */
  private openQuick(): void {
    if (this.quick) return;
    const screen = this.host.current;
    this.quickFromPlay = screen === 'hud' || screen === 'levelcard';
    if (this.quickFromPlay) this.host.show('paused');
    this.quick = true;
    this.modelDirty = true;
  }

  private closeQuick(): void {
    if (!this.quick) return;
    this.quick = false;
    this.modelDirty = true;
    // The same `UIAction` the pause screen's RESUME row sends, so the run comes
    // back exactly as it does from a menu somebody opened with a button.
    if (this.quickFromPlay) this.host.dispatch({ type: 'resume' });
  }

  /**
   * Step back from the comfort panel to the pause screen it is sitting on.
   *
   * `openQuick` already put that screen up, so this only has to stop covering
   * it. It is the whole of the way into a menu on a headset with no buttons:
   * plate, then this, and everything the pause screen carries is reachable.
   */
  private showMenu(): void {
    if (!this.quick) return;
    this.quick = false;
    this.quickFromPlay = false;
    this.modelDirty = true;
  }

  /**
   * Open and close the pack from the bound `inventory` action.
   *
   * On a desktop, Tab opens it — and `ui/ui.ts` reads that key *directly* out
   * of a `keydown` rather than off the bound action, because the same handler
   * has to know whether the seed field has focus. A headset has no Tab, so
   * without this the pack would be unreachable in VR, and the pack is where the
   * bandages are. Honouring the bound action here rather than changing the
   * keyboard path keeps the desktop handler exactly as it is; this only ever
   * runs inside a session, where no key can reach that handler anyway.
   */
  private togglePack(ctx: GameContext): void {
    if (!ctx.input.pressed('inventory')) return;
    // The map is a stop, not a layer: nothing opens over it. The DOM handler
    // refuses Tab for the same reason.
    if (this.host.mapFull) return;
    const screen = this.host.current;
    if (screen === 'hud' || screen === 'levelcard') this.host.show('inventory');
    else if (screen === 'inventory') this.host.show('hud');
  }

  private syncModel(screen: ScreenName, mapFull: boolean): void {
    // Anything that puts the player back on the floor takes the comfort panel
    // with it: the pause button, RESUME on the screen underneath, a restart.
    // One rule rather than an unsubscribe for each of them, and it cannot leave
    // a menu hanging in front of somebody who is playing.
    if (this.quick && (screen === 'hud' || screen === 'levelcard')) {
      this.quick = false;
      this.modelDirty = true;
    }
    if (screen !== this.lastScreen) {
      this.lastScreen = screen;
      this.modelDirty = true;
    }
    // The pack is the only thing that can change while its own screen is up,
    // and comparing a short string beats rebuilding a list of closures a frame.
    if (screen === 'inventory') {
      const key = fingerprint(this.inventory);
      if (key !== this.inventoryKey) {
        this.inventoryKey = key;
        this.modelDirty = true;
      }
    }
    if (this.modelDirty) {
      this.modelDirty = false;
      // The comfort panel is an overlay on whatever screen is up rather than a
      // screen of its own: `ui/ui.ts` owns the list of screens and there is no
      // DOM one for this, because on a monitor there is nothing to be comfortable
      // about.
      this.model = this.quick ? comfortScreen(this.deps) : buildScreen(screen, this.deps);
    }
    // The full map is its own stop: nothing else opens over it, so the menu
    // stands down rather than stacking behind a sheet of paper.
    const want = mapFull ? null : this.model;
    if (want === this.appliedModel) return;
    this.appliedModel = want;
    this.built?.menu.setModel(want);
  }

  /* ---------------------------------------------------------------- *
   * The map
   * ---------------------------------------------------------------- */

  /**
   * The map, which `ui/map.ts` has already drawn onto a canvas for us.
   *
   * In its corner form it goes on the player's left hand, if the session has
   * one in the scene — a map you raise to look at rather than one that floats
   * beside you. Full-screen it becomes a stop, and takes the middle of the view
   * the way the DOM one takes the screen.
   */
  private syncMap(ctx: GameContext, dt: number, mapFull: boolean): void {
    const b = this.built;
    if (!b) return;
    const visible = this.host.mapVisible;

    if (!b.map) {
      if (!visible) return;
      const canvas = this.host.mapCanvas();
      if (!canvas) return;
      b.map = new MirrorPanel(canvas, MAP_FULL_HEIGHT, MAP_FULL_HEIGHT);
      b.map.setOrder(1040);
    }

    const wrist = visible && !mapFull ? handGrip(ctx.render.renderer, 'left') : null;
    const mount: MapMount = !visible ? 'off' : mapFull ? 'full' : wrist ? 'wrist' : 'corner';
    if (mount !== this.mapMount || wrist !== this.mapWrist) {
      this.mapMount = mount;
      this.mapWrist = wrist;
      this.mountMap(b, mount, wrist);
    }
    if (mount === 'off') {
      b.map.setVisible(false);
      return;
    }

    const canvas = this.host.mapCanvas();
    const aspect = canvas && canvas.height > 0 ? clampAspect(canvas.width / canvas.height) : 1;
    const h = mount === 'wrist' ? MAP_WRIST_HEIGHT : mount === 'corner' ? MAP_CORNER_HEIGHT : MAP_FULL_HEIGHT;
    b.map.setSize(h * aspect, h);
    b.map.setVisible(true);

    // Throttled: this is a whole-canvas texture upload, and the sheet only
    // changes when the player crosses a cell or pans it — which, since the full
    // map halts the simulation, is almost never while it is being read.
    this.mapRefreshIn -= dt;
    if (this.mapRefreshIn <= 0) {
      this.mapRefreshIn = 1 / MAP_REFRESH_HZ;
      b.map.refresh();
    }
  }

  private mountMap(b: Built, mount: MapMount, wrist: THREE.Object3D | null): void {
    const object = b.map?.object;
    if (!object) return;
    switch (mount) {
      case 'wrist':
        if (!wrist) return;
        wrist.add(object);
        // Grip space: -Z points the way the hand points, +Y comes out of the
        // back of the hand. This sits the sheet just behind the knuckles,
        // tilted up toward the face — a watch, not a billboard.
        object.position.set(0, 0.045, 0.075);
        object.rotation.set(-Math.PI / 2 + 0.5, 0, 0);
        break;
      case 'full':
        b.menuDock.object.add(object);
        place(object, ZERO, MENU_DROP, MENU_DISTANCE);
        break;
      case 'corner':
        b.content.object.add(object);
        place(object, MAP_CORNER_AT, CONTENT_DROP, CONTENT_DISTANCE);
        break;
      default:
        object.removeFromParent();
        break;
    }
  }

  /* ---------------------------------------------------------------- *
   * Lifecycle
   * ---------------------------------------------------------------- */

  private activate(ctx: GameContext): Built {
    const b = this.built ?? this.build();
    ctx.render.scene.add(b.root);
    this.active = true;
    this.lastNow = performance.now();
    this.menuWasOpen = false;
    // Listening starts here and stops in `suspend`, so the page carries no XR
    // event handlers between sessions and none at all without one.
    this.pointer.attach(ctx.render.renderer);
    // Nothing that would already have gone from the DOM stack should be waiting
    // in the room for somebody who has only just put a headset on.
    b.toasts.prune();
    b.subtitle.clear();
    b.subtitle.setEnabled(this.host.settings.subtitles);
    if (this.lastSpec) b.card.set(this.lastSpec.name, this.lastSpec.subtitle, this.lastSpec.blurb);
    this.modelDirty = true;
    this.appliedModel = null;
    return b;
  }

  /**
   * Leaving a session. The group comes out of the scene entirely rather than
   * being hidden: an invisible subtree is skipped when drawing but still walked
   * when world matrices are updated, and this layer is supposed to cost nothing
   * at all once the headset is off.
   */
  private suspend(): void {
    this.built?.root.removeFromParent();
    // The map is the one panel that can be parented outside `root` — onto a
    // controller's grip, which belongs to whoever owns the session. Hand it
    // back rather than leaving a sheet of paper attached to their rig.
    this.built?.map?.object.removeFromParent();
    this.mapMount = 'off';
    this.mapWrist = null;
    this.pointer.detach();
    // The comfort panel is a state of this layer, not of the DOM UI, and a
    // session that ends with it up must not leave the next one holding it.
    this.quick = false;
    this.quickFromPlay = false;
    this.modelDirty = true;
    this.active = false;
  }

  private build(): Built {
    const root = new THREE.Group();
    root.name = 'xr-ui';

    const content = new Dock({
      distance: CONTENT_DISTANCE,
      deadZone: 0.32,
      rate: 3.2,
      drop: CONTENT_DROP,
      halfWidth: 0.5,
    });
    const menuDock = new Dock({
      // A menu tolerates more head movement before it follows — you look around
      // the room while deciding — but not much more: the panel is nearly forty
      // degrees wide, and a dead zone as generous as it sounds would leave its
      // far edge somewhere you have to turn your neck to read.
      distance: MENU_DISTANCE,
      deadZone: 0.4,
      rate: 2.5,
      drop: MENU_DROP,
      halfWidth: 0.45,
    });

    const prompt = new PromptPanel();
    const vitals = new VitalsPanel();
    const subtitle = new SubtitlePanel();
    const toasts = new ToastPanel();
    const card = new LevelCardPanel();
    const menu = new MenuPanel();
    const tab = new ComfortTab();
    const mark = new PressMark();

    place(prompt.panel.object, PROMPT_AT, CONTENT_DROP, CONTENT_DISTANCE);
    place(vitals.panel.object, VITALS_AT, CONTENT_DROP, CONTENT_DISTANCE);
    place(toasts.panel.object, TOASTS_AT, CONTENT_DROP, CONTENT_DISTANCE);
    place(subtitle.panel.object, SUBTITLE_AT, CONTENT_DROP, CONTENT_DISTANCE);
    place(card.panel.object, CARD_AT, CONTENT_DROP, CONTENT_DISTANCE);
    place(tab.panel.object, TAB_AT, CONTENT_DROP, CONTENT_DISTANCE);
    place(menu.panel.object, ZERO, MENU_DROP, MENU_DISTANCE);

    // Back to front. Overlaps are rare by construction, but a defined order
    // beats whatever the depth sort decides for two coplanar quads.
    prompt.panel.setOrder(1010);
    vitals.panel.setOrder(1011);
    toasts.panel.setOrder(1012);
    subtitle.panel.setOrder(1013);
    // Over the subtitle: their blocks are next to each other rather than on top
    // of each other, but the plate has to be pressable whatever is being said.
    tab.panel.setOrder(1015);
    card.panel.setOrder(1020);
    menu.panel.setOrder(1030);

    content.object.add(
      prompt.panel.object,
      vitals.panel.object,
      toasts.panel.object,
      subtitle.panel.object,
      tab.panel.object,
      card.panel.object,
    );
    menuDock.object.add(menu.panel.object);
    root.add(content.object, menuDock.object);

    const built: Built = { root, content, menuDock, prompt, vitals, subtitle, toasts, card, menu, tab, mark, map: null };
    this.built = built;
    return built;
  }

  dispose(): void {
    for (const off of this.off) off();
    this.off.length = 0;
    this.pointer.detach();
    const b = this.built;
    if (!b) return;
    b.prompt.dispose();
    b.vitals.dispose();
    b.subtitle.dispose();
    b.toasts.dispose();
    b.card.dispose();
    b.menu.dispose();
    b.tab.dispose();
    b.mark.dispose();
    b.map?.dispose();
    b.content.dispose();
    b.menuDock.dispose();
    b.root.removeFromParent();
    this.built = null;
    this.active = false;
  }
}

const ZERO = new THREE.Vector3(0, 0, 0);

/**
 * Turn a panel to face where the player's eyes are.
 *
 * A dock is one flat plane, so a panel half a metre off to the side is being
 * read at a twenty-degree slant unless it is toed in. Computed once at build
 * time: panels do not move on their dock.
 */
function place(object: THREE.Object3D, at: THREE.Vector3, drop: number, distance: number): void {
  object.position.copy(at);
  const dx = -at.x;
  const dy = drop - at.y;
  const dz = distance - at.z;
  object.rotation.set(-Math.atan2(dy, Math.hypot(dx, dz)), Math.atan2(dx, dz), 0, 'YXZ');
}

/** A sheet of paper is a sheet of paper; refuse anything pathological. */
function clampAspect(a: number): number {
  return Number.isFinite(a) ? Math.min(2.4, Math.max(0.42, a)) : 1;
}

/** Kinds and counts, as a string. Cheaper than rebuilding a menu per frame. */
function fingerprint(inventory: readonly InventorySlot[]): string {
  let s = '';
  for (const slot of inventory) s += `${slot.count > 0 ? slot.kind : '-'}${slot.count}|`;
  return s;
}

/**
 * Build the in-world UI layer.
 *
 * Cheap enough to call at boot on any device: it subscribes to the event bus
 * and allocates nothing else until an immersive session actually starts.
 */
export function makeXRUi(host: XRUiHost): XRUi {
  return new XRUiLayer(host);
}
